import { generateRandomFilename, triggerFileDownload } from "@/utils/helpers";
import { log } from "@/utils/logger";
import { useCallback, useState } from "react";
import useFileViewer, { FileDescriptor } from "@/components/common/modals/file-viewer/context";
import useQueryAccountingTransactionAttachment from "@/hooks/queries/useQueryAccountingTransactionAttachment";

const isExpiredAttachment = (attachment: FileDescriptor) => {
    if (!attachment.expiresAt) return false;
    return new Date(attachment.expiresAt).getTime() <= Date.now();
};

const useDownload = () => {
    const { fetchAccountingTransactionAttachment } = useQueryAccountingTransactionAttachment();
    const [loading, setLoading] = useState(false);

    const onDownload = async (transactionId: number, filename: string | null) => {
        try {
            setLoading(true);
            const data = await fetchAccountingTransactionAttachment(transactionId, "download");
            if (!data) {
                throw new Error("Could not fetch accounting transaction attachment");
            }
            const file: FileDescriptor = {
                url: data.url,
                filename: data.filename ?? filename ?? generateRandomFilename({ extension: "bin" }),
                mimeType: data.contentType || "",
                expiresAt: data.expiresAt,
            };
            triggerFileDownload(file);
        } catch (error) {
            log.error(error);
        } finally {
            setLoading(false);
        }
    };

    return { loading, onDownload };
};

const usePreview = (attachmentsCache: React.MutableRefObject<Map<number, FileDescriptor>>) => {
    const open = useFileViewer(state => state.open);
    const fetchAttachment = useFetch(attachmentsCache);
    const [loading, setLoading] = useState(false);

    const onPreview = async (transactionId: number, filename: string | null) => {
        const attachment = attachmentsCache.current.get(transactionId);

        if (attachment && !isExpiredAttachment(attachment)) return open(attachment);

        try {
            setLoading(true);
            const data = await fetchAttachment(transactionId, filename);
            open(data);
        } catch (error) {
            log.error(error);
        } finally {
            setLoading(false);
        }
    };

    return { loading, onPreview };
};

const useFetch = (attachmentsCache: React.MutableRefObject<Map<number, FileDescriptor>>) => {
    const { fetchAccountingTransactionAttachment } = useQueryAccountingTransactionAttachment();

    const fetchAttachment = useCallback(
        async (transactionId: number, filename: string | null) => {
            const attachment = attachmentsCache.current.get(transactionId);
            if (attachment && !isExpiredAttachment(attachment)) return attachment;

            const data = await fetchAccountingTransactionAttachment(transactionId, "preview");
            if (!data) {
                throw new Error("Could not fetch accounting transaction attachment");
            }
            const mimeType = data.contentType || "";

            const file: FileDescriptor = {
                url: data.url,
                filename: data.filename ?? filename ?? generateRandomFilename({ extension: "bin" }),
                mimeType,
                expiresAt: data.expiresAt,
            };

            attachmentsCache.current.set(transactionId, file);

            return file;
        },
        [attachmentsCache, fetchAccountingTransactionAttachment],
    );

    return fetchAttachment;
};

export { useDownload, usePreview };
